import React from 'react';
import Disclaimer from '../components/Disclaimer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, AlertTriangle, Phone, Mail } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-black">
      <Disclaimer />
      <Header />
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 glass rounded-full border border-toxic-green/30">
              <Shield className="w-5 h-5 text-toxic-green" />
              <span className="text-sm text-toxic-green font-semibold">Jeu responsable</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Jeu responsable
            </h1>
            <p className="text-xl text-white/70">
              Informations et ressources pour une pratique saine et responsable des paris sportifs
            </p>
          </div>

          <article className="glass-strong rounded-2xl p-8 md:p-12 border border-white/10 space-y-8 text-white/80 leading-relaxed">
            <section>
              <div className="glass rounded-xl p-6 border border-red-500/30 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Avertissement important</h3>
                    <p className="text-white/90">
                      Les paris sportifs peuvent être addictifs et causer des problèmes financiers, familiaux et personnels. 
                      Ne pariez jamais plus que ce que vous pouvez vous permettre de perdre. Si vous pensez avoir un problème 
                      de jeu, de l'aide est disponible.
                    </p>
                  </div>
                </div>
              </div>
              <p className="mb-4">
                Chez Rouge Bets, nous croyons fermement que les paris sportifs doivent rester un loisir agréable et contrôlé. 
                Cette page fournit des informations importantes sur le jeu responsable, les signes de problèmes potentiels, et 
                les ressources disponibles pour obtenir de l'aide.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Qu'est-ce que le jeu responsable ?</h2>
              <p className="mb-4">
                Le jeu responsable signifie parier de manière contrôlée et consciente, en gardant toujours à l'esprit que les 
                paris sportifs sont une forme de divertissement, pas un moyen de gagner de l'argent ou de résoudre des problèmes 
                financiers. Cela implique :
              </p>
              <ul className="list-disc list-inside mb-4 space-y-2">
                <li>Ne parier qu'avec de l'argent que vous pouvez vous permettre de perdre</li>
                <li>Fixer des limites de temps et d'argent avant de commencer</li>
                <li>Ne jamais parier sous l'influence de l'alcool ou de drogues</li>
                <li>Ne jamais essayer de "récupérer" vos pertes en augmentant vos mises</li>
                <li>Prendre des pauses régulières</li>
                <li>Ne jamais parier lorsque vous êtes émotionnellement perturbé</li>
                <li>Équilibrer les paris avec d'autres activités et responsabilités</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Signes d'un problème potentiel</h2>
              <p className="mb-4">
                Il est important de reconnaître les signes d'un problème de jeu potentiel. Si vous reconnaissez plusieurs de ces 
                signes, il peut être temps de demander de l'aide :
              </p>
              <ul className="list-disc list-inside mb-4 space-y-2">
                <li>Parier plus d'argent ou plus souvent que prévu</li>
                <li>Penser constamment aux paris et aux gains potentiels</li>
                <li>Augmenter les mises pour ressentir la même excitation</li>
                <li>Se sentir agité ou irritable lorsque vous ne pouvez pas parier</li>
                <li>Essayer de "récupérer" les pertes en parisant davantage</li>
                <li>Mentir à la famille ou aux amis sur vos habitudes de jeu</li>
                <li>Négliger le travail, les études ou les responsabilités familiales à cause des paris</li>
                <li>Emprunter de l'argent ou vendre des biens pour financer les paris</li>
                <li>Les paris causent des problèmes dans vos relations</li>
                <li>Se sentir coupable, anxieux ou déprimé à cause des paris</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Outils de jeu responsable</h2>
              <p className="mb-4">
                Les opérateurs agréés en France offrent plusieurs outils pour vous aider à maintenir une pratique responsable :
              </p>
              
              <h3 className="text-xl font-semibold text-white mb-3">Limites de dépôt</h3>
              <p className="mb-4">
                Vous pouvez fixer des limites quotidiennes, hebdomadaires ou mensuelles sur le montant que vous pouvez déposer. 
                Une fois la limite atteinte, vous devrez attendre la période suivante avant de pouvoir déposer à nouveau.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">Limites de perte</h3>
              <p className="mb-4">
                Vous pouvez fixer des limites sur le montant que vous êtes prêt à perdre sur une période donnée. Une fois la limite 
                atteinte, vous ne pourrez plus parier jusqu'à la période suivante.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">Limites de temps</h3>
              <p className="mb-4">
                Vous pouvez limiter le temps que vous passez à parier chaque jour ou chaque semaine. Le système vous rappellera lorsque 
                votre limite de temps est atteinte.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">Auto-exclusion</h3>
              <p className="mb-4">
                Vous pouvez demander une auto-exclusion temporaire ou permanente de votre compte. Pendant cette période, vous ne pourrez 
                pas accéder à votre compte ou placer des paris.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3">Reality checks</h3>
              <p className="mb-4">
                Des rappels automatiques peuvent vous informer du temps que vous avez passé à parier et du montant que vous avez dépensé, 
                vous aidant à rester conscient de votre activité.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ressources d'aide en France</h2>
              <p className="mb-4">
                Si vous pensez avoir un problème de jeu, de nombreuses ressources sont disponibles pour vous aider :
              </p>
              
              <div className="glass rounded-xl p-6 border border-toxic-green/30 mb-4">
                <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-toxic-green" />
                  Joueurs Info Service
                </h3>
                <p className="mb-2 text-white/90">
                  Service d'information et d'aide pour les joueurs et leur entourage
                </p>
                <ul className="list-none space-y-1">
                  <li><strong className="text-white">Site web :</strong> <a href="https://www.joueurs-info-service.fr" target="_blank" rel="noopener noreferrer" className="text-toxic-green hover:text-toxic-cyan underline">www.joueurs-info-service.fr</a></li>
                  <li><strong className="text-white">Téléphone :</strong> <a href="tel:0974751313" className="text-toxic-green hover:text-toxic-cyan underline">09 74 75 13 13</a> (appel non surtaxé)</li>
                  <li><strong className="text-white">Email :</strong> <a href="mailto:contact@joueurs-info-service.fr" className="text-toxic-green hover:text-toxic-cyan underline">contact@joueurs-info-service.fr</a></li>
                </ul>
              </div>

              <div className="glass rounded-xl p-6 border border-toxic-green/30 mb-4">
                <h3 className="text-xl font-semibold text-white mb-3">Autorité Nationale des Jeux (ANJ)</h3>
                <p className="mb-2 text-white/90">
                  Autorité de régulation des jeux en ligne en France
                </p>
                <ul className="list-none space-y-1">
                  <li><strong className="text-white">Site web :</strong> <a href="https://www.anj.fr" target="_blank" rel="noopener noreferrer" className="text-toxic-green hover:text-toxic-cyan underline">www.anj.fr</a></li>
                  <li><strong className="text-white">Interdiction de jeu :</strong> Vous pouvez demander une interdiction volontaire de jeu via l'ANJ</li>
                </ul>
              </div>

              <div className="glass rounded-xl p-6 border border-toxic-green/30">
                <h3 className="text-xl font-semibold text-white mb-3">SOS Joueurs</h3>
                <p className="mb-2 text-white/90">
                  Association d'aide aux joueurs en difficulté
                </p>
                <ul className="list-none space-y-1">
                  <li><strong className="text-white">Site web :</strong> <a href="https://www.sosjoueurs.org" target="_blank" rel="noopener noreferrer" className="text-toxic-green hover:text-toxic-cyan underline">www.sosjoueurs.org</a></li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Conseils pour une pratique responsable</h2>
              <ul className="list-disc list-inside mb-4 space-y-2">
                <li><strong className="text-white">Fixez un budget :</strong> Déterminez à l'avance combien vous pouvez vous permettre de dépenser et respectez cette limite</li>
                <li><strong className="text-white">Ne pariez jamais sous l'émotion :</strong> Évitez de parier lorsque vous êtes en colère, triste ou sous l'influence</li>
                <li><strong className="text-white">Traitez les paris comme un coût de divertissement :</strong> Considérez l'argent que vous misez comme le coût d'un loisir, pas comme un investissement</li>
                <li><strong className="text-white">Prenez des pauses :</strong> Faites régulièrement des pauses dans vos activités de paris</li>
                <li><strong className="text-white">Ne poursuivez pas vos pertes :</strong> Si vous perdez, acceptez la perte et ne tentez pas de récupérer en augmentant vos mises</li>
                <li><strong className="text-white">Équilibrez votre vie :</strong> Assurez-vous que les paris ne prennent pas le pas sur d'autres aspects importants de votre vie</li>
                <li><strong className="text-white">Parlez-en :</strong> Si vous avez des préoccupations, parlez-en à quelqu'un en qui vous avez confiance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Pour les proches</h2>
              <p className="mb-4">
                Si vous êtes préoccupé par les habitudes de jeu d'un proche, il est important d'aborder le sujet avec compassion et compréhension. 
                Encouragez-les à demander de l'aide et offrez votre soutien. Les ressources mentionnées ci-dessus peuvent également vous aider à 
                comprendre comment mieux soutenir votre proche.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Notre engagement</h2>
              <p className="mb-4">
                Rouge Bets s'engage à promouvoir le jeu responsable. Nous fournissons des informations sur les risques, encourageons l'utilisation 
                des outils de jeu responsable, et mettons en avant les ressources d'aide disponibles. Nous croyons que les paris sportifs peuvent 
                être un loisir agréable lorsqu'ils sont pratiqués de manière responsable et contrôlée.
              </p>
            </section>

            <section>
              <div className="glass-strong rounded-xl p-6 border border-toxic-green/30">
                <h3 className="text-xl font-bold text-white mb-4">Besoin d'aide immédiate ?</h3>
                <p className="text-white/90 mb-4">
                  Si vous avez besoin d'aide immédiate concernant un problème de jeu, contactez :
                </p>
                <ul className="list-none space-y-2">
                  <li className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-toxic-green" />
                    <a href="tel:0974751313" className="text-toxic-green hover:text-toxic-cyan underline font-semibold">09 74 75 13 13</a>
                    <span className="text-white/70">(Joueurs Info Service)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-toxic-green" />
                    <a href="mailto:info@rougebets.com" className="text-toxic-green hover:text-toxic-cyan underline">info@rougebets.com</a>
                    <span className="text-white/70">(Nous pouvons vous orienter vers les ressources appropriées)</span>
                  </li>
                </ul>
              </div>
            </section>
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

